/**
 * Extrai todas as variáveis de template Mustache de uma string HTML.
 *
 * Suporta:
 * - `{{variavel}}` — substituição simples
 * - `{{#variavel}}...{{/variavel}}` — bloco condicional
 *
 * @param html - String HTML contendo placeholders no formato `{{var}}`.
 * @returns Array de nomes de variáveis únicos, ordenados alfabeticamente.
 *
 * @example
 * ```typescript
 * const vars = extractTemplateVariables("<p>{{name}} - {{job_title}}</p>");
 * // ["job_title", "name"]
 * ```
 */
export function extractTemplateVariables(html: string): string[] {
  const variables = new Set<string>();

  // Placeholders simples: {{variavel}}
  const simpleRegex = /\{\{(\w+)\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = simpleRegex.exec(html)) !== null) {
    variables.add(match[1]);
  }

  // Blocos condicionais: {{#variavel}}...{{/variavel}}
  const blockRegex = /\{\{#(\w+)\}\}/g;
  while ((match = blockRegex.exec(html)) !== null) {
    variables.add(match[1]);
  }

  return Array.from(variables).sort();
}

/**
 * Nomes alternativos que um template pode usar para se referir ao mesmo campo do
 * formulário. `normalizeUserData` deriva uns dos outros, então uma assinatura que
 * usa `{{name}}` continua dependendo do campo "Nome".
 */
const FIELD_ALIASES: Record<string, readonly string[]> = {
  display_name: ["name", "first_name", "last_name"],
  job_title: ["job"],
  photo_base64: ["photo"],
  certifications: ["certifications_display"],
};

/**
 * Reúne os campos de formulário efetivamente usados por um conjunto de templates.
 *
 * Serve para o formulário de perfil só pedir o que alguma assinatura do usuário
 * realmente renderiza — não adianta pedir "Telefone 2" para quem não tem nenhum
 * template que exiba esse campo.
 *
 * @param htmls - HTML dos templates disponíveis para o usuário.
 * @returns Nomes de campos usados, já resolvidos via {@link FIELD_ALIASES}.
 *
 * @example
 * ```typescript
 * const usados = collectTemplateFields(["<p>{{name}} - {{ramal}}</p>"]);
 * // Set { "name", "ramal", "display_name" }
 * ```
 */
export function collectTemplateFields(htmls: readonly string[]): Set<string> {
  const raw = new Set<string>();
  for (const html of htmls) {
    for (const variable of extractTemplateVariables(html || "")) raw.add(variable);
  }

  const fields = new Set(raw);
  for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
    if (aliases.some((alias) => raw.has(alias))) fields.add(field);
  }
  return fields;
}

/**
 * Metadados de uma variável de template: tipo de input, se é obrigatória e label amigável.
 */
export interface VariableMeta {
  /** Nome da variável no template (ex: "display_name"). */
  name: string;
  /** Tipo de input HTML recomendado. */
  inputType: "text" | "email" | "tel" | "url" | "photo";
  /** Se o campo é obrigatório. */
  required: boolean;
  /** Label amigável para exibição no formulário. */
  label: string;
  /** Placeholder do input. */
  placeholder: string;
}

/**
 * Mapa de metadados para todas as variáveis de template conhecidas.
 * Cada entrada define como o campo deve ser renderizado no formulário dinâmico.
 */
const VARIABLE_META_MAP: Record<string, Omit<VariableMeta, "name">> = {
  display_name: { inputType: "text", required: true, label: "Nome", placeholder: "Nome Completo" },
  first_name: { inputType: "text", required: false, label: "Primeiro Nome", placeholder: "Ex: João" },
  last_name: { inputType: "text", required: false, label: "Sobrenome", placeholder: "Ex: Silva" },
  email: { inputType: "email", required: true, label: "E-mail", placeholder: "seu@email.com" },
  job_title: { inputType: "text", required: false, label: "Cargo", placeholder: "Ex: Gerente de Vendas" },
  office_phone_1: { inputType: "tel", required: false, label: "Telefone 1", placeholder: "Ex: (11) 4810-5438" },
  office_phone_2: { inputType: "tel", required: false, label: "Telefone 2", placeholder: "Ex: (11) 4810-5435" },
  mobile_phone: { inputType: "tel", required: false, label: "Celular", placeholder: "Ex: (11) 99999-9999" },
  street_address: { inputType: "text", required: false, label: "Endereço", placeholder: "Ex: Rua Exemplo, 123" },
  city: { inputType: "text", required: false, label: "Cidade", placeholder: "Ex: São Paulo" },
  state: { inputType: "text", required: false, label: "Estado", placeholder: "Ex: SP" },
  zip_code: { inputType: "text", required: false, label: "CEP", placeholder: "Ex: 04729-080" },
  country: { inputType: "text", required: false, label: "País", placeholder: "Ex: Brasil" },
  photo_base64: { inputType: "photo", required: false, label: "Foto", placeholder: "" },
  ramal: { inputType: "text", required: false, label: "Ramal", placeholder: "Ex: 167" },
  facebook_url: { inputType: "url", required: false, label: "Facebook", placeholder: "Ex: https://facebook.com/seu-perfil" },
  twitter_url: { inputType: "url", required: false, label: "Twitter", placeholder: "Ex: https://twitter.com/seu-perfil" },
  linkedin_url: { inputType: "url", required: false, label: "LinkedIn", placeholder: "Ex: https://linkedin.com/in/seu-perfil" },
  instagram_url: { inputType: "url", required: false, label: "Instagram", placeholder: "Ex: https://instagram.com/seu-perfil" },
  website_url: { inputType: "url", required: false, label: "Site", placeholder: "Ex: https://seusite.com.br" },
  certifications: { inputType: "text", required: false, label: "Certificações", placeholder: "Ex: Certificado 1; Certificado 2; Certificado 3" },
};

/**
 * Retorna os metadados para uma lista de nomes de variáveis.
 * Variáveis desconhecidas recebem metadados padrão (`text`, opcional).
 *
 * @param variableNames - Lista de nomes de variáveis extraídas do template.
 * @returns Array de `VariableMeta` ordenado: obrigatórios primeiro, depois alfabético.
 *
 * @example
 * ```typescript
 * const fields = getVariableMeta(["display_name", "ramal", "email"]);
 * // [
 * //   { name: "display_name", inputType: "text", required: true, ... },
 * //   { name: "email", inputType: "email", required: true, ... },
 * //   { name: "ramal", inputType: "text", required: false, ... },
 * // ]
 * ```
 */
export function getVariableMeta(variableNames: string[]): VariableMeta[] {
  return variableNames
    .map((name) => {
      const known = VARIABLE_META_MAP[name];
      if (known) {
        return { name, ...known };
      }
      // Fallback para variáveis desconhecidas
      return {
        name,
        inputType: "text" as const,
        required: false,
        label: name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        placeholder: "",
      };
    })
    .sort((a, b) => {
      // Obrigatórios primeiro
      if (a.required !== b.required) return a.required ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}
